<script setup>
import { nextTick, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AiAPI from 'custom/api/ai';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import AiTrace from './AiTrace.vue';

// Assistant › Playground: chat with the assistant as a customer would. Nothing reaches customers or conversations
// (HTTP tools do run, so they can be tested). Each answer shows what the assistant used and did.
const props = defineProps({
  assistant: { type: Object, required: true },
});

const { t } = useLocoI18n();

const turns = ref([]);
const draft = ref('');
const isThinking = ref(false);
const listRef = ref(null);

const scrollDown = async () => {
  await nextTick();
  listRef.value?.scrollTo({ top: listRef.value.scrollHeight });
};

const send = async () => {
  const content = draft.value.trim();
  if (!content || isThinking.value) return;
  turns.value.push({ role: 'user', content });
  draft.value = '';
  isThinking.value = true;
  scrollDown();
  try {
    const messages = turns.value
      .filter(turn => !turn.error)
      .map(turn => ({ role: turn.role, content: turn.content }));
    const { data } = await AiAPI.playground(props.assistant.id, messages);
    turns.value.push({
      role: 'assistant',
      content: data.reply,
      trace: {
        sources: data.sources,
        toolCalls: data.tool_calls,
        tokens: data.tokens,
        cost: data.cost,
        model: data.model,
      },
    });
  } catch (error) {
    turns.value.push({
      role: 'assistant',
      error: true,
      content: error.response?.data?.error || error.message,
    });
  } finally {
    isThinking.value = false;
    scrollDown();
  }
};

const reset = () => {
  turns.value = [];
};
</script>

<template>
  <div
    class="flex flex-col h-[36rem] rounded-xl outline outline-1 -outline-offset-1 outline-n-weak overflow-hidden"
  >
    <div
      class="flex items-center justify-between gap-2 px-4 py-2 border-b border-n-weak"
    >
      <span class="text-xs text-n-slate-11">
        {{ t('LOCO.AI.PLAYGROUND.NOTE') }}
      </span>
      <Button
        xs
        ghost
        slate
        icon="i-lucide-rotate-ccw"
        :label="t('LOCO.AI.PLAYGROUND.RESET')"
        :disabled="!turns.length"
        @click="reset"
      />
    </div>

    <div
      ref="listRef"
      class="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3"
    >
      <p
        v-if="!turns.length"
        class="m-auto max-w-sm text-center text-sm text-n-slate-11"
      >
        {{ t('LOCO.AI.PLAYGROUND.EMPTY', { name: assistant.name }) }}
      </p>
      <div
        v-for="(turn, index) in turns"
        :key="index"
        class="flex flex-col gap-1 max-w-[80%]"
        :class="turn.role === 'user' ? 'self-end items-end' : 'self-start'"
      >
        <div
          class="px-3 py-2 rounded-xl text-sm whitespace-pre-wrap break-words"
          :class="{
            'bg-n-brand text-white': turn.role === 'user',
            'bg-n-alpha-2 text-n-slate-12':
              turn.role === 'assistant' && !turn.error,
            'bg-n-ruby-3 text-n-ruby-11': turn.error,
          }"
        >
          {{ turn.content }}
        </div>
        <AiTrace v-if="turn.trace" v-bind="turn.trace" />
      </div>
      <div
        v-if="isThinking"
        class="self-start flex items-center gap-2 text-sm text-n-slate-11"
      >
        <Icon icon="i-lucide-loader-circle" class="size-4 animate-spin" />
        {{ t('LOCO.AI.PLAYGROUND.THINKING', { name: assistant.name }) }}
      </div>
    </div>

    <form class="flex gap-2 p-3 border-t border-n-weak" @submit.prevent="send">
      <input
        v-model="draft"
        type="text"
        class="!mb-0 flex-1"
        :placeholder="t('LOCO.AI.PLAYGROUND.PLACEHOLDER')"
        :disabled="isThinking"
      />
      <Button
        type="submit"
        icon="i-lucide-send-horizontal"
        :disabled="!draft.trim() || isThinking"
      />
    </form>
  </div>
</template>
